import orderStyles from '@crm/styles/order.module.css';
import {capitalize, smoothScrollToTop} from '@crm/utils/helpers';
import React, {useEffect} from 'react';
import {useAppDispatch, useAppSelector} from '@crm/services/store';
import {housekeeprApi} from '@crm/api/housekeepr-api';
import {skipToken} from '@reduxjs/toolkit/query';
import {MobilepayBlue, PaymentCardOptions} from '@crm/utils/images';
import createEventV2 from '@crm/utils/gtagv2';
import {cleaningActions, cleaningSelectors} from '@crm/services/cleaning-slice';
import {paymentIntervalDisplayString} from '@crm/api/models/common/housekeepr-api';
import {CleaningRedeemCode} from '@crm/components/cleaning/cleaning-redeem-code';
import {CLEANING_STEP_HOME} from '@crm/models/steps/cleaning_step_home';
import {CLEANING_STEP_PROGRAMME} from '@crm/models/steps/cleaning_step_programme';
import {CLEANING_STEP_TIME} from '@crm/models/steps/cleaning_step_time';
import {CLEANING_STEP_PAYMENT} from '@crm/models/steps/cleaning_step_payment';
import {CLEANING_STEP_SUBMITTING_ORDER} from '@crm/models/steps/cleaning_step_submitting_order';
import {authSelectors} from '@crm/services/auth-slice';

type CleaningPaymentCardProps = {hidden?: boolean};

export const CleaningPaymentCard = (props: CleaningPaymentCardProps) => {
    const dispatch = useAppDispatch();

    const currentStep = useAppSelector(cleaningSelectors.selectStep);
    const isOnThisPage = currentStep === CLEANING_STEP_PAYMENT;
    const canGoToNext = useAppSelector(cleaningSelectors.selectCanGoToNextStep);

    const selectedPaymentType = useAppSelector(cleaningSelectors.selectPaymentType);

    const selectedAddress = useAppSelector(cleaningSelectors.selectAddress);
    const selectedArea = useAppSelector(cleaningSelectors.selectArea);
    const selectedBasementCleaningArea = useAppSelector(cleaningSelectors.selectBasementCleaningArea);
    const selectedServiceId = useAppSelector(cleaningSelectors.selectServiceId);
    const selectedFrequencyId = useAppSelector(cleaningSelectors.selectFrequencyId);
    const selectedPaymentIntervalId = useAppSelector(cleaningSelectors.selectPaymentIntervalId);
    const selectedAvailability = useAppSelector(cleaningSelectors.selectAvailability);
    const authenticated = useAppSelector(authSelectors.selectAuthenticated);
    const canPay = useAppSelector((state) => state.cleaning.canPay);

    const {data: addressResult} = housekeeprApi.useRegisterAddressQuery(
        selectedAddress && isOnThisPage
            ? {
                  darId: selectedAddress.adresse.id,
              }
            : skipToken
    );

    const area = selectedArea ?? addressResult?.area;

    const {data: serviceResult} = housekeeprApi.useGetServiceQuery(
        selectedServiceId && isOnThisPage ? selectedServiceId : skipToken
    );

    const {data: paymentMethodAvailabilityResult} = housekeeprApi.useGetPaymentAvailabilityQuery(
        authenticated && isOnThisPage ? undefined : skipToken
    );

    const {data: paymentIntervalsResult} = housekeeprApi.useGetPaymentIntervalsQuery(
        selectedPaymentIntervalId !== undefined && isOnThisPage ? undefined : skipToken
    );

    const paymentInterval = paymentIntervalsResult?.find(
        (paymentInterval) => paymentInterval.id == selectedPaymentIntervalId
    );
    const showPaymentInterval = paymentIntervalsResult != null && paymentIntervalsResult.length > 1;

    const frequency = serviceResult?.frequencies.find((frequency) => frequency.id == selectedFrequencyId);

    useEffect(() => {
        if (!canPay && paymentMethodAvailabilityResult?.available) {
            dispatch(cleaningActions.setCanPay(true));
            if (!selectedPaymentType) {
                dispatch(cleaningActions.setPaymentType('card'));
            }
        }
    }, [authenticated, canPay, dispatch, paymentMethodAvailabilityResult?.available, selectedPaymentType]);

    const today = new Date();
    const firstVisitDate = selectedAvailability ? new Date(selectedAvailability.date) : new Date();
    let paymentPeriodStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    let paymentPeriodEnd = new Date(
        paymentPeriodStart.getFullYear(),
        paymentPeriodStart.getMonth() + (paymentInterval?.months ?? 1),
        0
    );

    while (selectedAvailability && paymentPeriodEnd.getTime() < firstVisitDate.getTime()) {
        paymentPeriodStart = new Date(paymentPeriodEnd.getFullYear(), paymentPeriodEnd.getMonth() + 1, 1);
        paymentPeriodEnd = new Date(
            paymentPeriodStart.getFullYear(),
            paymentPeriodStart.getMonth() + (paymentInterval?.months ?? 1),
            0
        );
    }

    return (
        <div className={props.hidden ? orderStyles.hidden : orderStyles.cardWrapper}>
            <div className={orderStyles.card}>
                <div className={orderStyles.orderSection}>
                    <h1 className={orderStyles.cardH1}>Betaling</h1>
                    {(frequency?.every ?? 0) == 0 ? (
                        <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                            Engangsbesøg trækkes automatisk efter endt udførsel.
                        </p>
                    ) : (
                        <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                            Første abonnementsbetaling sker umiddelbart efter bestilling og dækker dit forventede
                            forbrug i første betalingsperiode, som går fra starten af indeværende måned og{' '}
                            {paymentInterval?.months ?? '1, 3 eller 6'} måned{paymentInterval?.months == 1 ? '' : 'er'}{' '}
                            frem. Fremtidige betalinger sker d. 21. i sidste måned af betalingsperioden, og dækker dit
                            forventede forbrug i den næste betalingsperiode.
                        </p>
                    )}
                    <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                        Du kan med fordel benytte dig af servicefradraget på op til&nbsp;
                        {(17500).toLocaleString('da-DK', {
                            style: 'currency',
                            currency: 'DKK',
                            minimumFractionDigits: 0,
                        })}
                        &nbsp;fradrag pr. person.&nbsp;
                        <a
                            className={orderStyles.hyperlink}
                            target={'_blank'}
                            href={'https://skat.dk/borger/fradrag/servicefradrag/servicefradrag'}>
                            (Læs mere på skat.dk)
                        </a>
                    </p>
                    <div
                        className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap}`}>
                        {paymentMethodAvailabilityResult?.available ? (
                            <>
                                <p>
                                    Du har allerede en tilknyttet betalingsmetode, der vil blive brugt. Du kan til
                                    enhver tid ændre din betalingsmetode i app&apos;en.
                                </p>
                            </>
                        ) : (
                            <>
                                <div
                                    className={`${orderStyles.cardPaymentOption} ${
                                        selectedPaymentType == 'card' ? orderStyles.selected : ''
                                    }`}
                                    onClick={() => {
                                        dispatch(cleaningActions.setPaymentType('card'));
                                    }}>
                                    <img className={'mx-2'} src={PaymentCardOptions.src} alt={'Betal med kort'} />
                                </div>
                                <div
                                    className={`${orderStyles.cardPaymentOption} ${orderStyles.mobilepay} ${
                                        selectedPaymentType == 'mobilepaysubscriptions' ? orderStyles.selected : ''
                                    }`}
                                    onClick={() => {
                                        dispatch(cleaningActions.setPaymentType('mobilepaysubscriptions'));
                                    }}>
                                    <img className={'mx-2'} src={MobilepayBlue.src} alt={'Betal med MobilePay'} />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={orderStyles.orderSection}>
                    <h1 className={orderStyles.cardH1}>Kampagner & Rabatkoder</h1>
                    <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                        Hvis du har en kampagnekode, referralkode eller rabatkode, kan du indtaste den her. Bemærk at
                        referralkoder kun kan benyttes på abonnementer.
                    </p>
                    <div style={{marginTop: -12}}>
                        <CleaningRedeemCode />
                    </div>
                </div>
                <div className={orderStyles.orderSection}>
                    <h1 className={orderStyles.cardH1}>Bestillingsoversigt</h1>
                    <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                        Tjek at alt er som det skal være. Hvis ikke, kan du klikke på et punkt for at redigere. I
                        app&apos;en vil du kunne se et mere nøjagtigt ankomsttidspunkt.
                    </p>
                    <div
                        className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap} ${orderStyles.flexCrossStretch}`}>
                        <div
                            className={`${orderStyles.overviewCard} cursor-pointer`}
                            onClick={() => {
                                dispatch(cleaningActions.setPage(CLEANING_STEP_HOME));
                                document.querySelector('#address-content')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                });
                            }}>
                            <h2>Adresse</h2>
                            <p>{selectedAddress?.tekst ?? 'Ikke valgt endnu'}</p>
                        </div>
                        <div
                            className={`${orderStyles.overviewCard} cursor-pointer`}
                            onClick={() => {
                                dispatch(cleaningActions.setPage(CLEANING_STEP_HOME));
                                document.querySelector('#area-content')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                });
                            }}>
                            <h2>Areal</h2>
                            <p>
                                {area
                                    ? selectedBasementCleaningArea
                                        ? `${area} m² og ${selectedBasementCleaningArea} m² kælder`
                                        : `${area}m²`
                                    : 'Indlæser...'}
                            </p>
                        </div>
                        <div
                            className={`${orderStyles.overviewCard} cursor-pointer`}
                            onClick={() => {
                                dispatch(cleaningActions.setPage(CLEANING_STEP_PROGRAMME));
                                document.querySelector('#service-content')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                });
                            }}>
                            <h2>Rengøringstype</h2>
                            <p>
                                {serviceResult?.name ??
                                    (selectedServiceId ? `Service #${selectedServiceId}` : 'Ikke valgt endnu')}
                            </p>
                        </div>
                        <div
                            className={`${orderStyles.overviewCard} cursor-pointer`}
                            onClick={() => {
                                dispatch(cleaningActions.setPage(CLEANING_STEP_PROGRAMME));
                                document.querySelector('#frequency-content')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                });
                            }}>
                            <h2>Hvor ofte?</h2>
                            <p>{frequency?.nicified_string ?? 'Ikke valgt endnu'}</p>
                        </div>
                        {showPaymentInterval && paymentInterval && (
                            <div
                                className={`${orderStyles.overviewCard} cursor-pointer`}
                                onClick={() => {
                                    dispatch(cleaningActions.setPage(CLEANING_STEP_PROGRAMME));
                                    document.querySelector('#payment-interval-content')?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center',
                                    });
                                }}>
                                <h2>Betalingsinterval</h2>
                                <p>{paymentIntervalDisplayString(paymentInterval)}</p>
                            </div>
                        )}
                        <div
                            className={`${orderStyles.overviewCard} cursor-pointer`}
                            onClick={() => {
                                dispatch(cleaningActions.setPage(CLEANING_STEP_TIME));
                                document.querySelector('#availability-content')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                });
                            }}>
                            <h2>
                                {frequency?.every !== 0
                                    ? 'Tidsrum for ankomst ved første besøg'
                                    : 'Tidsrum for ankomst'}
                            </h2>
                            <p>
                                {selectedAvailability
                                    ? `${capitalize(
                                          new Date(selectedAvailability.date).toLocaleDateString('da-DK', {
                                              dateStyle: 'medium',
                                          })
                                      )}, ${selectedAvailability.time_slot_range}`
                                    : 'Ikke valgt endnu'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${orderStyles.flexRow} ${orderStyles.flexMainSpaceBetween} self-stretch`}>
                <div className={`${orderStyles.buttonWrapper} ${orderStyles.outline} w-[200px]`}>
                    <button
                        onClick={() => {
                            dispatch(cleaningActions.previousPage());
                            smoothScrollToTop();
                        }}>
                        Tilbage
                    </button>
                </div>
                <div className={`${orderStyles.buttonWrapper} ${!canGoToNext ? orderStyles.disabled : ''} w-[200px]`}>
                    <button
                        onClick={() => {
                            createEventV2('begin_checkout');
                            dispatch(cleaningActions.setPage(CLEANING_STEP_SUBMITTING_ORDER));
                            smoothScrollToTop();
                        }}
                        disabled={!canGoToNext}>
                        Bestil
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CleaningPaymentCard;
